import { isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  TemplateRef,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { ScreenSizes } from '../../../classes/screen-sizes.class';
import { ContextMenuOptions } from '../../../components/context-menu-global/context-menu-global.component';
import { getNodePath } from '../../../lib/get-event-path';
import { randomAlphaString } from '../../../lib/random-string';
import { sleep } from '../../../lib/sleep';
import { ContextMenuService } from '../../../services/context-menu.service';

@Component({
  selector: 'app-context-menu',
  templateUrl: './context-menu.component.html',
  styleUrls: ['./context-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class ContextMenuComponent implements OnInit, OnDestroy {
  private contextMenuService = inject(ContextMenuService);
  private platformId = inject(PLATFORM_ID);
  private check = inject(ChangeDetectorRef);
  private id = randomAlphaString(10);

  @Input() position:
    | 'auto'
    | 'left'
    | 'left-out'
    | 'center'
    | 'screen-center'
    | 'screen-right'
    | 'right'
    | 'right-out' = 'auto';
  @Input() verticalPosition = 'auto';
  @Input() options: ContextMenuOptions = {};
  @Input() contextHoverIdentifier: string;
  @Input() contextHoverIdentifiers: string[];
  @Input() disabled: boolean;
  @Output() onToggle = new EventEmitter();
  @ViewChild('contextMenu') contextMenu: TemplateRef<unknown>;
  @ViewChild('contextButton') contextButton: ElementRef;
  public _isOpen = signal(false);
  public isOpen = signal(false);
  private hover: boolean;
  private hoverDelayStart: number;
  lastOpenTime: number;
  destroyed$ = new Subject<void>();

  @HostListener('mouseover') async enter() {
    if (this.disabled) return;
    if (this.hover) return;
    if (ScreenSizes.screenSize() !== 'desktop') return;
    if (this.options.openOnHover) {
      this.hover = true;
      if (this.options.hoverDelay) {
        const start = Date.now();
        this.hoverDelayStart = start;
        await sleep(this.options.hoverDelay);
        if (this.hoverDelayStart !== start) return;
        if (!this.hover) return;
      }
      this.open();
    }
  }
  @HostListener('mouseleave') async leave() {
    if (this.disabled) return;
    if (ScreenSizes.screenSize() !== 'desktop') return;
    this.hover = false;
    if (this.lastOpenTime && Date.now() - this.lastOpenTime < 200) await sleep(200);
    await sleep(0);
    if (this.contextMenuService.onContextContentHover.value === this.id) return;
    if (this.options.openOnHover && !this.hover) {
      this.close();
    }
  }

  ngOnInit(): void {
    if (isPlatformServer(this.platformId)) return;
    this.contextMenuService.onContextContentHover.pipe(takeUntil(this.destroyed$)).subscribe(async hover => {
      if (hover === this.id) return;
      if (!this._isOpen()) return;
      await sleep(0);
      if (this.hover) return;
      if (ScreenSizes.screenSize() !== 'desktop') return;
      if (this.options.openOnHover) this.close();
    });
  }

  clickOpen(event: MouseEvent) {
    if (this.disabled) return;
    if (!this.options.allowClicks) event.preventDefault();
    if (this.options.openOnHover && ScreenSizes.screenSize() === 'desktop') return;
    if (!this.options.allowClicks) event?.stopPropagation();
    if (this._isOpen()) {
      return this.close();
    }
    this.open();
  }

  async open() {
    this.lastOpenTime = Date.now();
    const options = { ...this.options };
    if (ScreenSizes.screenSize() !== 'desktop') delete options.openOnHover;

    const clientEl = this.contextButton?.nativeElement;
    if (!clientEl) return;

    const path = getNodePath(clientEl);
    if (!path.some(el => el.nodeName === 'APP-CONTEXT-MENU-GLOBAL')) {
      this.contextMenuService.openContextMenus$.value.forEach(menu => {
        menu.component.close();
      });
    }

    await sleep(0); // needed, otherwise there is a race condition with opening and closing the same context menu

    this.contextMenuService.showContext({
      component: this,
      template: this.contextMenu,
      position: this.position,
      verticalPosition: this.verticalPosition,
      clientEl,
      options,
      contextHoverIdentifiers: this.contextHoverIdentifiers || [this.contextHoverIdentifier],
      id: this.id,
    });
    this._isOpen.set(true);
    this.isOpen.set(true);
    this.onToggle.emit(true);
    this.check.detectChanges();
  }

  async close() {
    if (!this._isOpen()) return;
    this._isOpen.set(false);
    this.contextMenuService.hideContext(this);
    this.onToggle.emit(false);
    await sleep(200);
    this.isOpen.set(false);
    this.check.detectChanges();
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
